/**
 * Centralized Query Keys for TanStack Query (React Query) v5
 *
 * Query keys follow a hierarchical structure to enable efficient cache invalidation
 * and prevent cache leakage between tenants.
 *
 * Key Structure: ['module', 'entity', tenantDomain, ...specificFilters]
 *
 * Example:
 * - ['workforce', 'employees', 'company.example.com']                           // All employees for tenant
 * - ['workforce', 'employees', 'company.example.com', { status: 'ACTIVE' }]    // Filtered list
 * - ['workforce', 'employees', 'company.example.com', 'emp-123']               // Single employee
 *
 * Best Practices:
 * 1. Always include tenantDomain as 3rd element to isolate tenant data
 * 2. Use specific keys for single entities (include ID)
 * 3. Use general keys for lists (include filters)
 * 4. Use nested structure for cache invalidation targeting
 *
 * Cache Invalidation Examples:
 * - After creating employee: invalidate ['workforce', 'employees', tenantDomain]
 * - After updating employee: invalidate ['workforce', 'employees', tenantDomain, employeeId]
 * - After deleting employee: invalidate all employee queries for tenant
 */

import type { EmployeeFilters, PaginationParams } from '@/types/workforce'

/**
 * Workforce Module Query Keys
 *
 * Organized by entity type with list and detail variants.
 * All keys include tenantDomain for tenant isolation.
 */
export const queryKeys = {
  workforce: {
    // Base key for all workforce queries (use for invalidating entire workforce cache)
    all: (tenantDomain: string) => ['workforce', tenantDomain] as const,

    /**
     * Employee Query Keys
     */
    employees: {
      // All employee queries for tenant
      all: (tenantDomain: string) => ['workforce', 'employees', tenantDomain] as const,

      /**
       * Employee list with optional filters and pagination
       * @param tenantDomain - Tenant identifier
       * @param filters - Optional filtering criteria (status, department, role, search, etc.)
       * @param pagination - Optional pagination params (limit, nextToken, sortBy, sortOrder)
       */
      list: (tenantDomain: string, filters?: EmployeeFilters, pagination?: PaginationParams) =>
        ['workforce', 'employees', tenantDomain, { type: 'list', filters, pagination }] as const,

      /**
       * Single employee detail
       * @param tenantDomain - Tenant identifier
       * @param employeeId - Employee unique identifier
       */
      detail: (tenantDomain: string, employeeId: string) =>
        ['workforce', 'employees', tenantDomain, employeeId] as const,

      /**
       * Employee statistics (dashboard metrics)
       * @param tenantDomain - Tenant identifier
       */
      stats: (tenantDomain: string) =>
        ['workforce', 'employees', tenantDomain, 'stats'] as const,

      /**
       * Employee autocomplete search
       * @param tenantDomain - Tenant identifier
       * @param search - Search term
       * @param options - Search options (limit, status)
       */
      autocomplete: (tenantDomain: string, search: string, options?: { limit?: number; status?: string }) =>
        ['workforce', 'employees', tenantDomain, { type: 'autocomplete', search, options }] as const,
    },

    /**
     * Department Query Keys
     */
    departments: {
      // All department queries for tenant
      all: (tenantDomain: string) => ['workforce', 'departments', tenantDomain] as const,

      /**
       * Department list with optional pagination
       * @param tenantDomain - Tenant identifier
       * @param pagination - Optional pagination params
       */
      list: (tenantDomain: string, pagination?: PaginationParams) =>
        ['workforce', 'departments', tenantDomain, { type: 'list', pagination }] as const,

      /**
       * Single department detail
       * @param tenantDomain - Tenant identifier
       * @param departmentId - Department unique identifier
       */
      detail: (tenantDomain: string, departmentId: string) =>
        ['workforce', 'departments', tenantDomain, departmentId] as const,
    },

    /**
     * Role Query Keys
     */
    roles: {
      // All role queries for tenant
      all: (tenantDomain: string) => ['workforce', 'roles', tenantDomain] as const,

      /**
       * Role list with optional pagination
       * @param tenantDomain - Tenant identifier
       * @param pagination - Optional pagination params
       */
      list: (tenantDomain: string, pagination?: PaginationParams) =>
        ['workforce', 'roles', tenantDomain, { type: 'list', pagination }] as const,

      /**
       * Single role detail
       * @param tenantDomain - Tenant identifier
       * @param roleId - Role unique identifier
       */
      detail: (tenantDomain: string, roleId: string) =>
        ['workforce', 'roles', tenantDomain, roleId] as const,
    },

    /**
     * Payroll Query Keys
     */
    payroll: {
      // All payroll queries for tenant
      all: (tenantDomain: string) => ['workforce', 'payroll', tenantDomain] as const,

      /**
       * Payroll list with optional filters and pagination
       * @param tenantDomain - Tenant identifier
       * @param filters - Optional filtering criteria (period, employeeId, type, status)
       * @param pagination - Optional pagination params
       */
      list: (
        tenantDomain: string,
        filters?: { period?: string; employeeId?: string; type?: string; status?: string },
        pagination?: PaginationParams
      ) =>
        ['workforce', 'payroll', tenantDomain, { type: 'list', filters, pagination }] as const,

      /**
       * Single payroll record detail
       * @param tenantDomain - Tenant identifier
       * @param payrollId - Payroll unique identifier
       */
      detail: (tenantDomain: string, payrollId: string) =>
        ['workforce', 'payroll', tenantDomain, payrollId] as const,

      /**
       * Payroll summary for specific period
       * @param tenantDomain - Tenant identifier
       * @param period - Period in YYYY-MM format
       */
      periodSummary: (tenantDomain: string, period: string) =>
        ['workforce', 'payroll', tenantDomain, 'summary', period] as const,
    },

    /**
     * Organizational Hierarchy Query Keys
     */
    hierarchy: {
      /**
       * Complete organizational hierarchy tree
       * @param tenantDomain - Tenant identifier
       */
      tree: (tenantDomain: string) =>
        ['workforce', 'hierarchy', tenantDomain, 'tree'] as const,

      /**
       * Manager's direct reports
       * @param tenantDomain - Tenant identifier
       * @param managerId - Manager employee ID
       */
      directReports: (tenantDomain: string, managerId: string) =>
        ['workforce', 'hierarchy', tenantDomain, 'manager', managerId] as const,
    },

    /**
     * Dashboard and Analytics Query Keys
     */
    dashboard: {
      /**
       * Complete workforce dashboard data
       * @param tenantDomain - Tenant identifier
       */
      overview: (tenantDomain: string) =>
        ['workforce', 'dashboard', tenantDomain, 'overview'] as const,

      /**
       * Payroll dashboard data
       * @param tenantDomain - Tenant identifier
       * @param period - Optional period filter (defaults to current)
       */
      payrollSummary: (tenantDomain: string, period?: string) =>
        ['workforce', 'dashboard', tenantDomain, 'payroll', period] as const,
    },
  },
}

/**
 * Utility function to invalidate all queries for a specific entity type
 *
 * Usage:
 * ```typescript
 * // Invalidate all employee queries
 * queryClient.invalidateQueries({ queryKey: queryKeys.workforce.employees.all(tenantDomain) })
 *
 * // Invalidate specific employee
 * queryClient.invalidateQueries({ queryKey: queryKeys.workforce.employees.detail(tenantDomain, employeeId) })
 * ```
 */
export const invalidateWorkforceQueries = {
  /**
   * Invalidate all workforce queries for a tenant
   */
  all: (tenantDomain: string) => ({ queryKey: queryKeys.workforce.all(tenantDomain) }),

  /**
   * Invalidate all employee queries for a tenant
   */
  employees: (tenantDomain: string) => ({ queryKey: queryKeys.workforce.employees.all(tenantDomain) }),

  /**
   * Invalidate specific employee queries
   */
  employee: (tenantDomain: string, employeeId: string) => ({
    queryKey: queryKeys.workforce.employees.detail(tenantDomain, employeeId)
  }),

  /**
   * Invalidate all department queries for a tenant
   */
  departments: (tenantDomain: string) => ({ queryKey: queryKeys.workforce.departments.all(tenantDomain) }),

  /**
   * Invalidate all role queries for a tenant
   */
  roles: (tenantDomain: string) => ({ queryKey: queryKeys.workforce.roles.all(tenantDomain) }),

  /**
   * Invalidate all payroll queries for a tenant
   */
  payroll: (tenantDomain: string) => ({ queryKey: queryKeys.workforce.payroll.all(tenantDomain) }),

  /**
   * Invalidate all hierarchy queries for a tenant
   */
  hierarchy: (tenantDomain: string) => ({ queryKey: ['workforce', 'hierarchy', tenantDomain] }),

  /**
   * Invalidate all dashboard queries for a tenant
   */
  dashboard: (tenantDomain: string) => ({ queryKey: ['workforce', 'dashboard', tenantDomain] }),
}

/**
 * Budget Module Query Keys
 *
 * Query keys for financial settings management (PROA budgets, training budgets, client budgets).
 * All keys include tenantDomain for tenant isolation.
 *
 * Stale Time: 5 minutes (configured globally in queryClient for budget endpoints - AC1)
 */
export const budgetQueryKeys = {
  /**
   * Client Budget Query Keys
   */
  clientBudgets: {
    /**
     * All client budget queries for tenant
     */
    all: (tenantDomain: string) => ['budgets', 'clients', tenantDomain] as const,

    /**
     * Client budget list for specific year
     * @param tenantDomain - Tenant identifier
     * @param year - Budget year (e.g., 2025)
     */
    list: (tenantDomain: string, year: number) =>
      ['budgets', 'clients', tenantDomain, year] as const,

    /**
     * Single client budget for specific year
     * @param tenantDomain - Tenant identifier
     * @param clientCode - Client code (e.g., "000054")
     * @param year - Budget year (e.g., 2025)
     */
    detail: (tenantDomain: string, clientCode: string, year: number) =>
      ['budgets', 'clients', tenantDomain, clientCode, year] as const,
  },

  /**
   * Training Budget Query Keys
   */
  trainingBudget: {
    /**
     * All training budget queries for tenant
     */
    all: (tenantDomain: string) => ['budgets', 'training', tenantDomain] as const,

    /**
     * Training budget for specific year
     * @param tenantDomain - Tenant identifier
     * @param year - Budget year (e.g., 2025)
     */
    detail: (tenantDomain: string, year: number) =>
      ['budgets', 'training', tenantDomain, year] as const,
  },

  /**
   * Projections Summary Query Keys
   */
  projections: {
    /**
     * All projections queries for tenant
     */
    all: (tenantDomain: string) => ['budgets', 'projections', tenantDomain] as const,

    /**
     * Company-wide projections summary for specific year
     * @param tenantDomain - Tenant identifier
     * @param year - Budget year (e.g., 2025)
     */
    summary: (tenantDomain: string, year: number) =>
      ['budgets', 'projections', tenantDomain, year] as const,
  },
}

/**
 * Utility function to invalidate budget queries (cache invalidation - AC2)
 *
 * Usage:
 * ```typescript
 * // After saving client budget:
 * queryClient.invalidateQueries(invalidateBudgetQueries.clientBudget(tenantDomain, clientCode, year))
 *
 * // After saving training budget:
 * queryClient.invalidateQueries(invalidateBudgetQueries.trainingBudget(tenantDomain, year))
 * ```
 */
export const invalidateBudgetQueries = {
  /**
   * Invalidate all client budget queries for tenant
   */
  allClientBudgets: (tenantDomain: string) => ({
    queryKey: budgetQueryKeys.clientBudgets.all(tenantDomain)
  }),

  /**
   * Invalidate specific client budget
   */
  clientBudget: (tenantDomain: string, clientCode: string, year: number) => ({
    queryKey: budgetQueryKeys.clientBudgets.detail(tenantDomain, clientCode, year)
  }),

  /**
   * Invalidate client budgets list for year
   */
  clientBudgetsList: (tenantDomain: string, year: number) => ({
    queryKey: budgetQueryKeys.clientBudgets.list(tenantDomain, year)
  }),

  /**
   * Invalidate training budget for year
   */
  trainingBudget: (tenantDomain: string, year: number) => ({
    queryKey: budgetQueryKeys.trainingBudget.detail(tenantDomain, year)
  }),

  /**
   * Invalidate projections summary for year
   */
  projectionsSummary: (tenantDomain: string, year: number) => ({
    queryKey: budgetQueryKeys.projections.summary(tenantDomain, year)
  }),
}
